/* StepFields — Combined wizard step 3.
 *
 * Two panels in one step (save together):
 *   • Top — Field visibility toggles for the 16 canonical product fields
 *   • Bottom — Custom data points editor (up to 5 user-defined fields)
 *
 * Right side — live BuyerViewPreview that reflects BOTH visibility +
 * custom-field changes in real time. Single Save fires two API calls in
 * parallel (PUT /visibility + PUT /custom-fields) so the seller doesn't
 * have to think about ordering. */
import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Eye, EyeOff, Settings2, ChevronLeft, Save, Plus, Trash2, Tag, Sliders,
} from "lucide-react";
import BuyerViewPreview from "../catalog-import/BuyerViewPreview";

const FIELD_LABELS = {
  sku:          { l: "SKU code",            d: "Internal product code" },
  name:         { l: "Product name",        d: "Required — always visible" },
  brand:        { l: "Brand",               d: "Manufacturer / vendor name" },
  category:     { l: "Category",            d: "Filter buyers can browse by" },
  description:  { l: "Description",         d: "Long-form product details" },
  price:        { l: "Wholesale price",     d: "Required — always visible" },
  retail_price: { l: "Retail (MSRP)",       d: "Shows margin to the buyer" },
  moq:          { l: "MOQ",                 d: "Minimum order quantity" },
  stock:        { l: "Stock on hand",       d: "Live inventory count" },
  case_size:    { l: "Case size",           d: "Units per case (for Unit/Case toggle)" },
  upc:          { l: "UPC / barcode",       d: "Most sellers hide this from buyers" },
  image:        { l: "Image",               d: "Product photo" },
  fulfillment:  { l: "Fulfillment",         d: "Wholesale / Dropship / Both" },
  badge:        { l: "Badges",              d: "New arrival, top seller, weekly special, …" },
  map_price:    { l: "MAP price",           d: "Minimum advertised price" },
  tags:         { l: "Tags / keywords",     d: "Search keywords" },
};
const FORCED_VISIBLE = new Set(["name", "price"]);

const CF_TYPES = [
  { v: "text",     l: "Text",       hint: "Free-form — material, finish, scent…" },
  { v: "number",   l: "Number",     hint: "Numeric — SPF, capacity, weight…" },
  { v: "dropdown", l: "Dropdown",   hint: "Pick one of pre-set options" },
  { v: "yes_no",   l: "Yes / No",   hint: "Binary — certified organic, vegan…" },
];

function blankCf() {
  return {
    key: "", label: "", type: "text", options: [],
    default_value: null, visible_to_buyers: true, filterable: false,
  };
}

function autoSlug(label) {
  return (label || "").trim().toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "") || "field";
}

export default function StepFields({ onNext, onBack, onSaved }) {
  // Field visibility state
  const [visibility, setVisibility] = useState({});
  // Custom-fields state
  const [customFields, setCustomFields] = useState([]);
  const [maxCf, setMaxCf] = useState(5);
  // Misc
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sample, setSample] = useState(null);

  // Load everything in parallel
  useEffect(() => {
    (async () => {
      try {
        const [v, cf, p] = await Promise.all([
          api.get("/seller/catalog-import/visibility"),
          api.get("/seller/catalog-setup/custom-fields"),
          api.get("/products?limit=1").catch(() => ({ data: [] })),
        ]);
        setVisibility(v.data?.fields || {});
        setCustomFields((cf.data?.fields || []).map(f => ({ ...f, options: f.options || [] })));
        setMaxCf(cf.data?.max || 5);
        const arr = p.data?.products || p.data || [];
        if (Array.isArray(arr) && arr[0]) setSample(arr[0]);
      } catch (_e) { /* keep defaults */ }
      finally { setLoading(false); }
    })();
  }, []);

  // ─── visibility helpers ───
  const toggleVis = (k) => {
    if (FORCED_VISIBLE.has(k)) return;
    setVisibility(f => ({ ...f, [k]: !f[k] }));
  };

  // ─── custom-fields helpers ───
  const addCf = () => {
    if (customFields.length >= maxCf) return;
    setCustomFields([...customFields, blankCf()]);
  };
  const updateCf = (i, patch) => {
    setCustomFields(customFields.map((f, idx) => {
      if (idx !== i) return f;
      const next = { ...f, ...patch };
      if (patch.label !== undefined && (!f.key || f.key === autoSlug(f.label))) {
        next.key = autoSlug(patch.label);
      }
      if (patch.type !== undefined && !["dropdown", "yes_no"].includes(patch.type)) {
        next.filterable = false;
      }
      return next;
    }));
  };
  const removeCf = (i) => setCustomFields(customFields.filter((_, idx) => idx !== i));

  // Sample product for live preview — inject demo values for any custom field
  // the seller has defined but the real product doesn't carry yet.
  const previewProduct = useMemo(() => {
    if (!sample) return null;
    const base = { ...sample };
    const cfs = base.custom_fields ? { ...base.custom_fields } : {};
    customFields.forEach(f => {
      if (cfs[f.key]) return;
      if (f.type === "yes_no") cfs[f.key] = "Yes";
      else if (f.type === "dropdown" && f.options?.[0]) cfs[f.key] = f.options[0];
      else if (f.type === "number") cfs[f.key] = "12";
      else cfs[f.key] = "Sample value";
    });
    base.custom_fields = cfs;
    return base;
  }, [sample, customFields]);

  // Save both in parallel
  const saveAndNext = async () => {
    setBusy(true);
    try {
      // Light client validation for custom fields
      for (const f of customFields) {
        if (!f.label?.trim()) throw new Error("Every custom field needs a label");
        if (f.type === "dropdown" && (f.options || []).length === 0)
          throw new Error(`Dropdown "${f.label}" needs at least one option`);
      }
      const cleanVis = { ...visibility };
      FORCED_VISIBLE.forEach(k => { cleanVis[k] = true; });
      await Promise.all([
        api.put("/seller/catalog-import/visibility", { fields: cleanVis }),
        api.put("/seller/catalog-setup/custom-fields", { fields: customFields }),
      ]);
      onSaved && onSaved();
      onNext();
    } catch (e) {
      toast.error(e.message || e.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="setup-step-fields">
      <div className="text-[10px] mono uppercase tracking-[0.24em] text-foreground/55">Step 3 of 4 · Fields &amp; custom data</div>
      <h1 className="display text-3xl mt-2 tracking-tight">Customize what buyers see</h1>
      <p className="text-sm text-foreground/60 mt-2 max-w-2xl">
        Toggle off any standard fields you don&apos;t want exposed. Then add up to <strong>{maxCf}</strong> custom data points your products carry (material, country of origin, certifications…). The preview on the right updates in real time.
      </p>

      <div className="mt-7 grid lg:grid-cols-[1fr_320px] gap-6">
        {/* LEFT — standard visibility + custom fields editor */}
        <div className="space-y-8">
          {loading && <div className="py-12 text-center text-foreground/45 text-sm">Loading…</div>}

          {!loading && (
            <>
              {/* ── Section A: standard field visibility ─────────── */}
              <section data-testid="step-fields-visibility">
                <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/55 mb-3 inline-flex items-center gap-1.5">
                  <Eye className="w-3 h-3"/>Standard field visibility
                </div>
                <div className="space-y-1.5">
                  {Object.entries(FIELD_LABELS).map(([k, meta]) => {
                    const on = !!visibility[k] || FORCED_VISIBLE.has(k);
                    const forced = FORCED_VISIBLE.has(k);
                    return (
                      <button key={k} onClick={() => toggleVis(k)} disabled={forced}
                        data-testid={`vis-${k}`}
                        className={`w-full text-left rounded-xl border p-3 flex items-start gap-3 transition-colors ${forced ? "border-border bg-secondary/40 cursor-default" : "border-border bg-white hover:border-foreground/40"}`}>
                        <span className={`shrink-0 w-9 h-5 rounded-full p-0.5 flex items-center transition-colors ${on ? "bg-foreground" : "bg-secondary border border-border"}`}>
                          <span className="w-4 h-4 rounded-full bg-white transition-transform" style={{ transform: on ? "translateX(14px)" : "translateX(0)" }}/>
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium inline-flex items-center gap-2">
                            {meta.l}
                            {forced && <span className="chip bg-foreground/10 text-foreground/55 text-[9px]">Required</span>}
                          </div>
                          <div className="text-[11px] text-foreground/55 mt-0.5">{meta.d}</div>
                        </div>
                        <span className="text-foreground/45 shrink-0">{on ? <Eye className="w-4 h-4"/> : <EyeOff className="w-4 h-4"/>}</span>
                      </button>
                    );
                  })}
                </div>
              </section>

              {/* ── Section B: custom data points ─────────────────── */}
              <section data-testid="step-fields-custom">
                <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/55 mb-1.5 inline-flex items-center gap-1.5">
                  <Sliders className="w-3 h-3"/>Custom data points (optional)
                </div>
                <p className="text-[11.5px] text-foreground/55 mb-3 max-w-prose">
                  Define up to {maxCf} fields beyond the standard ones — e.g. material, country of origin, certifications. AI will look for matching columns during import.
                </p>
                <div className="space-y-3">
                  {customFields.length === 0 && (
                    <div className="rounded-xl border border-dashed border-border bg-secondary/20 p-5 text-center text-foreground/55 text-[12.5px]">
                      No custom fields yet — click <strong>Add field</strong> below, or skip.
                    </div>
                  )}
                  {customFields.map((f, i) => (
                    <CfRow key={i} field={f} update={(p) => updateCf(i, p)} remove={() => removeCf(i)} index={i}/>
                  ))}
                  <button onClick={addCf} disabled={customFields.length >= maxCf}
                    data-testid="add-custom-field"
                    className="w-full h-10 rounded-xl border border-dashed border-foreground/30 hover:border-foreground/60 text-[12.5px] inline-flex items-center justify-center gap-1.5 text-foreground/65 hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed">
                    <Plus className="w-3.5 h-3.5"/>Add custom field {customFields.length > 0 ? `(${customFields.length}/${maxCf})` : ""}
                  </button>
                </div>
              </section>
            </>
          )}
        </div>

        {/* RIGHT — live preview (sticky) */}
        <div className="lg:sticky lg:top-4 self-start">
          <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/55 mb-2 inline-flex items-center gap-1.5">
            <Settings2 className="w-3 h-3"/>Buyer view preview
          </div>
          <BuyerViewPreview product={previewProduct} visibility={visibility} customFields={customFields}/>
        </div>
      </div>

      <div className="mt-8 flex items-center justify-between">
        <button onClick={onBack} className="h-10 px-4 rounded-full text-sm text-foreground/65 hover:text-foreground inline-flex items-center gap-1.5"><ChevronLeft className="w-3.5 h-3.5"/>Back</button>
        <button onClick={saveAndNext} disabled={busy || loading} data-testid="fields-next"
          className="h-11 px-5 rounded-full bg-foreground text-background hover:bg-foreground/90 text-sm font-medium disabled:opacity-50">
          {busy ? "Saving…" : <><Save className="w-3.5 h-3.5 mr-1.5 inline-block"/>Save &amp; continue →</>}
        </button>
      </div>
    </div>
  );
}

/* ─────────────── Single custom field row editor (compact) ──────────── */
function CfRow({ field, update, remove, index }) {
  return (
    <div className="rounded-xl border border-border bg-white p-3.5" data-testid={`cf-row-${index}`}>
      <div className="grid grid-cols-[1fr_140px_auto] gap-2.5">
        <div>
          <label className="text-[9.5px] mono uppercase tracking-[0.22em] text-foreground/55">Label</label>
          <input value={field.label}
            onChange={(e) => update({ label: e.target.value })}
            placeholder='e.g. "Material"'
            data-testid={`cf-label-${index}`}
            className="mt-1 w-full h-9 rounded-md border border-input bg-white px-2.5 text-[12.5px] focus:outline-none focus:border-foreground/40"/>
          {field.key && <div className="text-[9.5px] text-foreground/45 mt-0.5 mono">key: {field.key}</div>}
        </div>
        <div>
          <label className="text-[9.5px] mono uppercase tracking-[0.22em] text-foreground/55">Type</label>
          <select value={field.type} onChange={(e) => update({ type: e.target.value })}
            data-testid={`cf-type-${index}`}
            className="mt-1 w-full h-9 rounded-md border border-input bg-white px-2 text-[12.5px]">
            {CF_TYPES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
        </div>
        <button onClick={remove}
          data-testid={`cf-remove-${index}`}
          className="self-end h-9 w-9 rounded-md text-foreground/45 hover:text-rose-600 hover:bg-rose-50 grid place-items-center" title="Remove">
          <Trash2 className="w-3.5 h-3.5"/>
        </button>
      </div>
      {field.type === "dropdown" && (
        <div className="mt-2.5">
          <label className="text-[9.5px] mono uppercase tracking-[0.22em] text-foreground/55">Options · comma-separated</label>
          <input value={(field.options || []).join(", ")}
            onChange={(e) => update({ options: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
            placeholder="USA, Italy, France, Portugal"
            data-testid={`cf-options-${index}`}
            className="mt-1 w-full h-9 rounded-md border border-input bg-white px-2.5 text-[12.5px] focus:outline-none focus:border-foreground/40"/>
        </div>
      )}
      <div className="mt-2.5 flex items-center gap-4 text-[11.5px] flex-wrap">
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={!!field.visible_to_buyers}
            onChange={(e) => update({ visible_to_buyers: e.target.checked })}
            data-testid={`cf-visible-${index}`}
            className="accent-foreground"/>
          Visible to buyers
        </label>
        {(field.type === "dropdown" || field.type === "yes_no") && (
          <label className="inline-flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={!!field.filterable}
              onChange={(e) => update({ filterable: e.target.checked })}
              data-testid={`cf-filterable-${index}`}
              className="accent-foreground"/>
            Filterable
          </label>
        )}
        {field.type === "text" && (
          <span className="text-[10px] text-foreground/45 inline-flex items-center gap-1"><Tag className="w-2.5 h-2.5"/>{CF_TYPES.find(t => t.v === field.type)?.hint}</span>
        )}
      </div>
    </div>
  );
}
